import type { ClonePackagePayload, PackageCloneData } from '@openx/types/package';
import type { UsePackageClone } from '@openx/types/package/PackageHookTypes';
import { useMutation } from '@tanstack/react-query';
import type { GraphQLFormattedError } from 'graphql/error';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClonePackageDocument,
  type ClonePackageMutationVariables,
} from '../../../graphql/mutation/packages/ClonePackage.generated';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';
import { transformTargetingForMutation } from './transformTargetingForMutation';

export const usePackageClone: UsePackageClone = () => {
  const { t } = useTranslation();
  const { mutateAsync: cloneMutation } = useMutation({
    mutationFn: (variables: ClonePackageMutationVariables, { client }) =>
      getGraphQLRequestFromClient(client)(ClonePackageDocument, variables),
  });
  const { enqueueSnackbar } = useSnackbar();
  const [isCloning, setIsCloning] = useState<boolean>(false);

  useLocalErrorHandling(ClonePackageDocument, () => true);

  const clonePackage = useCallback(
    async ({ packages, editModePayload }: ClonePackagePayload) => {
      setIsCloning(true);
      const clonedPackageResults: Record<number, PackageCloneData> = {};

      const fulfilled: { origin: PackageCloneData; clone: PackageCloneData }[] = [];
      const errors: { graphQLErrors?: GraphQLFormattedError[]; packageId: string }[] = [];

      const promises = packages.map(async (pckg, index) => {
        const transformedPayload = editModePayload
          ? {
              ...editModePayload,
              targeting: transformTargetingForMutation(editModePayload.targeting),
            }
          : undefined;

        const res = await cloneMutation({ id: pckg.id, package: transformedPayload });

        if (res.clonePackage) {
          clonedPackageResults[index] = res.clonePackage;
        }
      });

      const result = await Promise.allSettled(promises.filter(item => item));

      result.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          fulfilled.push({ clone: clonedPackageResults[index], origin: packages[index] });
        } else {
          errors.push({ graphQLErrors: result.reason.graphQLErrors, packageId: packages[index].id });
        }
      });

      errors.forEach(({ graphQLErrors }) => {
        const message = graphQLErrors?.[0]?.extensions?.errors?.[0]?.message
          ? graphQLErrors[0].extensions.errors[0].message
          : t('Failed to clone package.');

        enqueueSnackbar(message, { variant: 'error' });
      });

      if (packages.length === 1 && fulfilled.length) {
        const message = t('Package {packageName} successfully cloned.', { packageName: packages[0].name });
        enqueueSnackbar(message, { variant: 'success' });
      } else if (fulfilled.length === packages.length) {
        const message = t('Packages successfully cloned.');
        enqueueSnackbar(message, { variant: 'success' });
      } else if (fulfilled.length) {
        const message = t('{cloned} of {all} was successfully cloned.', {
          all: packages.length,
          cloned: fulfilled.length,
        });
        enqueueSnackbar(message, { variant: 'success' });
      }

      setIsCloning(false);

      return { errors, fulfilled };
    },
    [cloneMutation, enqueueSnackbar, t]
  );

  return { clonePackage, isCloning };
};
